import { useState } from "react";

import { Box, Typography } from "@mui/material";

import CompanyDataListItem from "./CompanyDataListItem";

import PlacesAutocomplete from "../../inputs/autocomplete/PlacesAutocomplete";
import DbAutocomplete from "../../inputs/autocomplete/DbAutocomplete";

import {
  useCachedFirebaseCrud,
  useCachedDisplayData
} from "../../../helpers/firebase";
import { fetchAddressEGrid } from "../../../helpers/utils/apiCalls";

const CompanyAddressRow = ({ address = {}, isRemote, primaryOffice }) => {
  const { updateAccountData } = useCachedFirebaseCrud();
  const [offices] = useCachedDisplayData("offices");

  const [saveAddressLoading, setSaveAddressLoading] = useState(false);
  const [modifyData, setModifyData] = useState(address);
  const { description: addressDescription } = address || {};

  const updateAddress = async (newData) => {
    const { id } = newData || {};

    if (isRemote || !offices.length) {
      // fetchAddressEGrid resolves to the eGrid string itself, not an object;
      // destructuring it left remote companies with no stored eGrid at all.
      const eGrid = await fetchAddressEGrid(newData);
      return updateAccountData({ address: { ...newData, eGrid } });
    }

    setSaveAddressLoading(true);

    const { address, utilities } =
      offices.find((office) => office.id === id) || {};
    const { eGrid: officeEgrid } = utilities || {};

    const eGrid = officeEgrid || (await fetchAddressEGrid(address));

    const newAddress = { ...address, eGrid };

    updateAccountData({ address: newAddress, primaryOffice: newData });
    setModifyData(newData);
    return setSaveAddressLoading(false);
  };

  return (
    <CompanyDataListItem
      editingComponent={
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Choose your primary office
          </Typography>
          {(isRemote && !primaryOffice) || !offices.length ? (
            <PlacesAutocomplete
              place={modifyData}
              size="small"
              editPlace={setModifyData}
              label="Choose a new address"
              style={{ width: "250px" }}
              variant="standard"
            />
          ) : (
            <DbAutocomplete
              collection="offices"
              value={modifyData || ""}
              setValue={(newValue) => {
                setModifyData(newValue);
                if (newValue) {
                  updateAddress(newValue);
                }
              }}
              queries={[["type", "==", "companyOffice"]]}
              size="small"
              style={{ width: "250px" }}
              label={(() => {
                const matchedOffice = offices.find(
                  (office) => office.address?.placeId === modifyData?.placeId
                );

                return matchedOffice
                  ? matchedOffice?.name || "Unknown office"
                  : "";
              })()}
              variant="standard"
            />
          )}
        </Box>
      }
      saveDisabled={!modifyData}
      onCancel={() => setModifyData(address)}
      onSave={() => updateAddress(modifyData)}
      primaryText={addressDescription || "Address not set"}
      secondaryText="Company Address"
      primaryTypographyProps={
        isRemote && !modifyData?.address?.description ? { color: "error" } : {}
      }
      saveLoading={saveAddressLoading}
    />
  );
};

export default CompanyAddressRow;
